import { tenantSelector } from "@/react/features/customer/customerSlice";
import { useSelector } from "react-redux";
import {
  handleCreateResponse,
  handleDeleteResponse,
  handleUpdateResponse,
  useCreateEmailChannelsMutation,
  useDeleteEmailChannelsMutation,
  useGetEmailChannelsQuery,
  useUpdateEmailChannelsMutation,
} from "./emailChannelsApi";
import { useTable } from "@/react/components/table/useTable";
import schema from "./schema";
import columnDefinition from "./columnDefinition";
import TableComponent from "@/react/components/table/TableComponent";
import React, { useCallback, useMemo } from "react";
import { errorNotification, useErrorNotification } from "@/react/notifications";
import { useGetProfileQuery } from "../profile/profileApi";
import { appendIdFromKey } from "@/react/components/table/useTableMutations";
import TableWrapper from "@/react/features/styled/TableWrapper";
import PageContentContainer from "@/react/features/styled/PageContentContainer";
import PageTitle from "@/react/components/PageTitle";

export const EmailChannelsPage = () => {
  const tenant = useSelector(tenantSelector);
  const tenantName = tenant.tenantId;
  const { data: profile } = useGetProfileQuery(tenantName);

  const emailChannelsColumns = useMemo(() => {
    return columnDefinition();
  }, [profile]);

  const {
    data: initialData,
    isFetching: isLoading,
    error,
    refetch,
  } = useGetEmailChannelsQuery(tenantName);

  const [createEmailChannels, { isLoading: isCreating }] =
    useCreateEmailChannelsMutation();
  const [updateEmailChannels, { isLoading: isUpdating }] =
    useUpdateEmailChannelsMutation();
  const [deleteEmailChannels, { isLoading: isDeleting }] =
    useDeleteEmailChannelsMutation();

  useErrorNotification(error);

  const { table, filters, state, mutations } = useTable(
    initialData,
    emailChannelsColumns,
    schema,
    schema.fields.id.spec.key
  );

  const {
    rowsToCreate,
    rowsToUpdate,
    rowsToDelete,
    resetData,

    setData,
    setRowsToCreate,
    setRowsToUpdate,
    setRowsToDelete,
  } = mutations;

  const isDirty =
    rowsToCreate.size > 0 || rowsToUpdate.size > 0 || rowsToDelete.size > 0;

  const onSave = useCallback(() => {
    if (rowsToCreate.size > 0) {
      createEmailChannels({
        tenantName,
        emailChannels: appendIdFromKey(
          Array.from(rowsToCreate.values()),
          schema.fields.id.spec.key
        ),
      })
        .unwrap()
        .then(handleCreateResponse({ rowsToCreate, setRowsToCreate, setData }))
        .catch(errorNotification);
    }

    if (rowsToUpdate.size > 0) {
      updateEmailChannels({
        tenantName,
        emailChannels: Array.from(rowsToUpdate.values()),
      })
        .unwrap()
        .then(handleUpdateResponse({ rowsToUpdate, setRowsToUpdate, setData }))
        .catch(errorNotification);
    }

    if (rowsToDelete.size > 0) {
      deleteEmailChannels({
        tenantName,
        emailChannelIds: Array.from(rowsToDelete.keys()),
      })
        .unwrap()
        .then(handleDeleteResponse({ rowsToDelete, setRowsToDelete, setData }))
        .catch(errorNotification);
    }
  }, [rowsToCreate, rowsToUpdate, rowsToDelete]);

  const isTableLoading = isLoading || isCreating || isUpdating || isDeleting;

  return (
    <PageContentContainer>
      <PageTitle>Email Channels</PageTitle>
      <TableWrapper>
        <TableComponent
          title="Email Channels"
          schema={schema}
          isDirty={isDirty}
          isLoading={isTableLoading}
          onSave={onSave}
          onCancel={resetData}
          onRefresh={refetch}
          table={table}
          filters={filters}
          mutations={mutations}
          state={state}
        />
      </TableWrapper>
    </PageContentContainer>
  );
};
